import { useState } from 'react';
import { motion } from 'framer-motion';
import { FileText, Users, BarChart, Settings, Plus } from 'lucide-react';

const AdminArea = () => {
  const [activeTab, setActiveTab] = useState('posts');

  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  const stats = [
    { label: 'Total Posts', value: '24', icon: FileText, color: 'from-blue-500 to-cyan-500' },
    { label: 'Active Users', value: '156', icon: Users, color: 'from-purple-500 to-pink-500' },
    { label: 'Page Views', value: '12.4K', icon: BarChart, color: 'from-green-500 to-emerald-500' },
  ];

  const recentPosts = [
    { id: 1, title: 'Getting Started with Web3', status: 'Published', views: 543, date: '2024-01-20' },
    { id: 2, title: 'React Performance Tips', status: 'Draft', views: 0, date: '2024-01-18' },
    { id: 3, title: 'AI Ethics Guide', status: 'Published', views: 892, date: '2024-01-15' },
  ];

  return (
    <div className="pt-32 pb-24">
      <div className="container-custom max-w-7xl">
        {/* Header */}
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeIn}
          className="mb-8"
        >
          <div className="flex items-center justify-between">
            <div>
              <h1 className="mb-2">Admin Dashboard</h1>
              <p className="text-gray-600 dark:text-gray-400">
                Manage your content, users, and site settings
              </p>
            </div>
            <button className="btn-primary inline-flex items-center">
              <Plus size={20} className="mr-2" />
              Create New Post
            </button>
          </div>
        </motion.div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          {stats.map((stat, index) => (
            <motion.div
              key={stat.label}
              initial="hidden"
              animate="visible"
              variants={fadeIn}
              transition={{ delay: index * 0.1 }}
              className="card"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 dark:text-gray-400 text-sm mb-1">{stat.label}</p>
                  <p className="text-3xl font-bold">{stat.value}</p>
                </div>
                <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${stat.color} flex items-center justify-center`}>
                  <stat.icon className="text-white" size={28} />
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Tabs */}
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeIn}
          transition={{ delay: 0.3 }}
          className="mb-6"
        >
          <div className="border-b border-gray-200 dark:border-gray-700">
            <div className="flex space-x-8">
              {['posts', 'users', 'settings'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`pb-4 px-2 capitalize font-medium transition-colors ${
                    activeTab === tab
                      ? 'border-b-2 border-[rgb(var(--color-accent))] text-[rgb(var(--color-accent))]'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                  }`}
                >
                  {tab}
                </button>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Content Area */}
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeIn}
          transition={{ delay: 0.4 }}
        >
          {activeTab === 'posts' && (
            <div className="card">
              <h3 className="text-xl font-semibold mb-6">Recent Posts</h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-700">
                      <th className="text-left py-3 px-4 font-semibold">Title</th>
                      <th className="text-left py-3 px-4 font-semibold">Status</th>
                      <th className="text-left py-3 px-4 font-semibold">Views</th>
                      <th className="text-left py-3 px-4 font-semibold">Date</th>
                      <th className="text-left py-3 px-4 font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentPosts.map((post) => (
                      <tr key={post.id} className="border-b border-gray-200 dark:border-gray-700">
                        <td className="py-4 px-4">{post.title}</td>
                        <td className="py-4 px-4">
                          <span className={`px-3 py-1 rounded-full text-sm ${
                            post.status === 'Published'
                              ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400'
                              : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400'
                          }`}>
                            {post.status}
                          </span>
                        </td>
                        <td className="py-4 px-4">{post.views}</td>
                        <td className="py-4 px-4 text-gray-600 dark:text-gray-400">
                          {new Date(post.date).toLocaleDateString()}
                        </td>
                        <td className="py-4 px-4">
                          <button className="text-[rgb(var(--color-accent))] hover:underline mr-4">
                            Edit
                          </button>
                          <button className="text-red-600 hover:underline">
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'users' && (
            <div className="card">
              <h3 className="text-xl font-semibold mb-6">User Management</h3>
              <p className="text-gray-600 dark:text-gray-400">
                User management features will be displayed here. You can view, edit, and manage user accounts.
              </p>
            </div>
          )}

          {activeTab === 'settings' && (
            <div className="card">
              <h3 className="text-xl font-semibold mb-6">Site Settings</h3>
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium mb-2">Site Title</label>
                  <input
                    type="text"
                    defaultValue="Professional Portfolio"
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Site Description</label>
                  <textarea
                    defaultValue="A personal website showcasing technical expertise and insights"
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800"
                  />
                </div>
                <div>
                  <button className="btn-primary">Save Settings</button>
                </div>
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default AdminArea;
